import type { Division, SoftwareCompany } from '../shared/types';
import { authenticatedFetch } from '../shared/authenticatedFetch';
import { ApiError } from '../shared/apiError';

const FALLBACK_DIVISION_NAMES = [
  "APAC",
  "Central",
  "ERP",
  "Health & Social Care",
  "Hospitality",
  "Legal",
  "Non-P&E",
  "NPE",
  "Operations",
  "Payments",
  "People",
  "Recruitment",
];

const fallbackDivisions: Division[] = FALLBACK_DIVISION_NAMES.map((name) => ({
  id: name.toLowerCase(),
  name,
}));

export async function getDivisions(apiUrl: string): Promise<Division[]> {
  try {
    const response = await authenticatedFetch(`${apiUrl}/divisions`);

    const data = await response.json();
    const divisionNames: string[] = Array.isArray(data) ? data : (data.items ?? []);
    const divisions: Division[] = divisionNames.map((name) => ({
      id: name.toLowerCase(),
      name,
    }));
    console.info(`ProjectApproval_ Fetched ${divisions.length} divisions from database`);
    return divisions;
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      throw error;
    }
    console.warn('ProjectApproval_ [FALLBACK] Divisions API call failed, returning fallback divisions', error);
    return fallbackDivisions;
  }
}

const FALLBACK_SOFTWARE_COMPANY_NAMES = [
  "APAC - Accounting & Tax",
  "APAC - ERP",
  "APAC - People & Payroll",
  "Central",
  "ERP - Construction",
  "ERP - Core",
  "ERP - Small Business",
  "ERP - Specialised",
  "HOS - Accommodation",
  "HOS - Food and Beverage",
  "HSC - Care",
  "HSC - Healthcare",
  "HSC - Local Gov & Tech",
  "Legal",
  "NPE",
  "PPL - Learn",
  "PPL - MLB",
  "PPL - SMB",
  "Payments",
  "Recruitment",
];

const fallbackSoftwareCompanies: SoftwareCompany[] = FALLBACK_SOFTWARE_COMPANY_NAMES.map((name) => ({
  id: name.toLowerCase().replace(/\s+/g, '-'),
  name,
  division_id: '',
}));

export async function getSoftwareCompanies(apiUrl: string): Promise<SoftwareCompany[]> {
  try {
    const response = await authenticatedFetch(`${apiUrl}/software-companies`);

    const data = await response.json();
    const companyNames: string[] = Array.isArray(data) ? data : (data.items ?? []);
    const companies: SoftwareCompany[] = companyNames.map((name) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      division_id: name?.split(' - ')[0].toLowerCase() || '',
    }));
    console.info(`ProjectApproval_ Fetched ${companies.length} software companies from database`);
    return companies;
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      throw error;
    }
    console.warn('ProjectApproval_ [FALLBACK] Software companies API call failed, returning fallback software companies', error);
    return fallbackSoftwareCompanies;
  }
}
